import {API} from 'aws-amplify';
import {createSlice} from '@reduxjs/toolkit';

import {ENDPOINT, IN_PROGRESS, PENDING, COMPLETE} from '../../constants';

function startLoading(state) {
  state.isLoading = true;
}

function loadingFailed(state, action) {
  state.isLoading = false;
  state.error = action.payload;
}

const initialState = Object.freeze({
  isLoading: false,
  activeStepIndex: null,
  activeExerciseIndex: null,
  exercise: null,
  steps: [],
});

export const lessonSlice = createSlice({
  name: 'lesson',
  initialState,
  reducers: {
    reset: () => initialState,
    getLessonStart: startLoading,
    getLessonSuccess: (state, action) => {
      state.steps = action.payload.steps.map((step) => ({
        ...step,
        status: PENDING,
        exercises: step.exercises.map((exerciseID) => ({...action.payload.exercises[exerciseID], isComplete: false})),
      }));
      state.isLoading = false;
      state.error = null;
    },
    getLessonFailure: loadingFailed,
    getExerciseStart: startLoading,
    getExerciseSuccess: (state, action) => {
      state.exercise = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    getExerciseFailure: loadingFailed,
    setActiveStep: (state, action) => {
      if ([IN_PROGRESS, COMPLETE].includes(state.steps[action.payload].status)) {
        state.activeStepIndex = action.payload;
        state.activeExerciseIndex = 0;
      }
    },
    setActiveExercise: (state, action) => {
      if (action.payload) {
        state.activeStepIndex = action.payload.stepIndex;
        state.activeExerciseIndex = action.payload.exerciseIndex;
      } else {
        const nextStepIndex = state.steps.findIndex((step) => step.status !== COMPLETE);
        const nextStep = state.steps[nextStepIndex];
        const nextExerciseIndex = nextStep.exercises.findIndex(exercise => !exercise.isComplete);
        
        state.activeStepIndex = nextStepIndex;
        state.activeExerciseIndex = nextExerciseIndex;
      }
    },
    completeExercise: (state) => {
      state.steps[state.activeStepIndex].exercises[state.activeExerciseIndex].isComplete = true;

      // If on last exercise, mark this step as complete
      if (state.steps[state.activeStepIndex].exercises.length === state.activeExerciseIndex + 1) {
        state.steps[state.activeStepIndex].status = COMPLETE;

        // If there are more steps, mark next step as active
        if (state.steps.length > state.activeStepIndex + 1) {
          state.steps[state.activeStepIndex + 1].status = IN_PROGRESS;

          state.activeStepIndex += 1; // Set next step as active
          state.activeExerciseIndex = 0; // Start on first exercise of new step
        }
      } else {
        // There are more exercises, set next as active
        state.activeExerciseIndex += 1;
      }
    },
    resetProgress: (state) => {
      state.steps.forEach(step => {
        step.status = PENDING;

        step.exercises.forEach(exercise => {
          exercise.isComplete = false;
        })
      })
    }
  },
});

export const {
  reset,
  getLessonStart,
  getLessonSuccess,
  getLessonFailure,
  getExerciseStart,
  getExerciseSuccess,
  getExerciseFailure,
  setActiveStep,
  setActiveExercise,
  completeExercise,
  resetProgress,
} = lessonSlice.actions;

export const setAssignmentLesson = (lesson) => (dispatch) => {
  try {
    dispatch(getLessonStart());

    dispatch(getLessonSuccess(lesson));
    dispatch(setActiveExercise());
  } catch (error) {
    dispatch(getLessonFailure(error.toString()));
  }
};

export const setAssignmentExercise = (exercise) => (dispatch) => {
  try {
    dispatch(getExerciseStart());
    dispatch(getExerciseSuccess(exercise));
  } catch (error) {
    dispatch(getExerciseFailure(error.toString()));
  }
};

export const fetchLesson = (lessonID) => async (dispatch) => {
  try {
    dispatch(getLessonStart());
    const lesson = await API.get(ENDPOINT, `/lessons/${lessonID}`);

    // lesson exercises
    const exerciseIDs = Array.from(new Set(lesson.steps.reduce((acc, value) => acc.concat(value.exercises), [])));
    const promises = exerciseIDs.map((exerciseID) => API.get(ENDPOINT, `/exercises/${exerciseID}`));
    const exercises = (await Promise.all(promises)).reduce(
      (acc, val) => ({
        ...acc,
        [val._id]: val,
      }),
      {},
    );
    lesson.exercises = exercises;
    
    dispatch(getLessonSuccess(lesson));
    dispatch(setActiveExercise());
  } catch (error) {
    dispatch(getLessonFailure(error.toString()));
  }
};

export const fetchExercise = (exerciseID) => async (dispatch) => {
  try {
    dispatch(getExerciseStart());
    const exercise = await API.get(ENDPOINT, `/exercises/${exerciseID}`);
    dispatch(getExerciseSuccess(exercise));
  } catch (error) {
    dispatch(getExerciseFailure(error.toString()));
  }
};

export const selectSteps = (state) => state.lesson.steps;
export const selectExercise = (state) => state.lesson.exercise;
export const selectIsLoading = (state) => state.lesson.isLoading;
export const selectActiveExercise = (state) => {
  return state.lesson.steps[state.lesson.activeStepIndex]?.exercises[state.lesson.activeExerciseIndex];
};

export default lessonSlice.reducer;
